import { Chart, ChartFooterChard, Typography, colors, IconGraphy } from '@leapeasy/ui-kit';
import { CLAIM_FILE_STATUS_CHART_LABELS } from 'data/constants/common_constants';

const claimFileStatuses = CLAIM_FILE_STATUS_CHART_LABELS;

export const ClaimFileStatusChart = ({ data, onClickItem }) => {
  const toNumber = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
  };

  const monthlyAggregates = (
    Array.isArray(data?.claimCountsByMonth) ? data.claimCountsByMonth : []
  ).filter((aggregate) => aggregate && typeof aggregate === 'object');

  const totalClaims = {
    open_test_claim: 0,
    open_pending_decision: 0,
    open_pending_payment: 0,
    closed_denied: 0,
    closed_paid: 0,
    closed_incident_only: 0,
    closed_partial_denial: 0,
    closed_duplicate: 0,
    closed_inactive: 0,
    incident_only: 0,
  };
  let sum = 0;

  for (const aggregate of monthlyAggregates) {
    for (let key in claimFileStatuses) {
      if (!totalClaims[key]) {
        totalClaims[key] = 0;
      }

      const claimCount = toNumber(aggregate?.[key]);
      totalClaims[key] += claimCount;
      sum += claimCount;
    }
  }

  if (!sum) sum = 1;

  return (
    <Chart
      variant="pie"
      pieMinAngle={30}
      data={Object.keys(totalClaims).map((statusKey) => ({
        name: claimFileStatuses[statusKey],
        value: (totalClaims[statusKey] / sum) * 100,
      }))}
      colors={[
        colors.parisGreen[100],
        colors.purpleIrish[100],
        colors.warmBlue[100],
        colors.magentaRed[100],
        colors.sunglow[100],
        colors.tomato[100],
        colors.tomato[200],
        colors.sunglow[200],
        colors.warmBlue[200],
        colors.magentaRed[200],
      ]}
      chartWidth={500}
      title="Claim File Status"
      description="This section provides a comprehensive breakdown of claims file status, showing the distribution across various categories."
      icon={<IconGraphy icon="FileFolder.Description" />}
      footer={
        <div style={{ gap: '16px', width: '100%', display: 'flex' }}>
          <div
            style={{
              gap: '8px',
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            <ChartFooterChard
              backgroundColor="#ECF9F4"
              activeColor={colors.parisGreen[100]}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {Number(totalClaims.open_test_claim).toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: colors.parisGreen[100],
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    Open Claim - Test Claim
                  </Typography>
                </div>
              }
              onClick={() => onClickItem?.('open_test_claim')}
            />

            <ChartFooterChard
              backgroundColor={colors.tomato[25]}
              activeColor={colors.tomato[100]}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {Number(totalClaims.closed_incident_only).toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: colors.tomato[100],
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    Closed - Incident Only
                  </Typography>
                </div>
              }
              onClick={() => onClickItem?.('closed_incident_only')}
            />
          </div>

          <div
            style={{
              gap: '8px',
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            <ChartFooterChard
              backgroundColor={colors.purpleIrish[25]}
              activeColor={colors.purpleIrish[100]}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {Number(totalClaims.open_pending_decision).toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: colors.purpleIrish[100],
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    Open - Pending Decision
                  </Typography>
                </div>
              }
              onClick={() => onClickItem?.('open_pending_decision')}
            />

            <ChartFooterChard
              backgroundColor={colors.parisGreen[25]}
              activeColor={colors.parisGreen[100]}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {Number(totalClaims.closed_partial_denial).toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: colors.parisGreen[100],
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    Closed - Partial Denial
                  </Typography>
                </div>
              }
              onClick={() => onClickItem?.('closed_partial_denial')}
            />
          </div>

          <div
            style={{
              gap: '8px',
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            <ChartFooterChard
              backgroundColor={colors.warmBlue[25]}
              activeColor={colors.warmBlue[100]}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {Number(totalClaims.open_pending_payment).toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: colors.warmBlue[100],
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    Open - Pending Payment
                  </Typography>
                </div>
              }
              onClick={() => onClickItem?.('open_pending_payment')}
            />

            <ChartFooterChard
              backgroundColor={colors.sunglow[50]}
              activeColor={colors.sunglow[100]}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {Number(totalClaims.closed_duplicate).toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: colors.sunglow[100],
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    Closed Claim - Duplicate
                  </Typography>
                </div>
              }
              onClick={() => onClickItem?.('closed_duplicate')}
            />
          </div>

          <div
            style={{
              gap: '8px',
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            <ChartFooterChard
              backgroundColor={colors.magentaRed[25]}
              activeColor={colors.magentaRed[100]}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {Number(totalClaims.closed_denied).toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: colors.magentaRed[100],
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    Closed - Claim Denied
                  </Typography>
                </div>
              }
              onClick={() => onClickItem?.('closed_denied')}
            />

            <ChartFooterChard
              backgroundColor={colors.warmBlue[50]}
              activeColor={colors.warmBlue[100]}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {Number(totalClaims.closed_inactive).toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: colors.warmBlue[100],
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    Closed Claim Inactive
                  </Typography>
                </div>
              }
              onClick={() => onClickItem?.('closed_inactive')}
            />
          </div>

          <div
            style={{
              gap: '8px',
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            <ChartFooterChard
              backgroundColor={colors.sunglow[25]}
              activeColor={colors.sunglow[100]}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {Number(totalClaims.closed_paid).toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: colors.sunglow[100],
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    Closed - Claim Paid
                  </Typography>
                </div>
              }
              onClick={() => onClickItem?.('closed_paid')}
            />

            <ChartFooterChard
              backgroundColor={colors.magentaRed[50]}
              activeColor={colors.magentaRed[100]}
              title={
                <Typography variant="h4" style={{ fontWeight: '500', color: colors.purple[900] }}>
                  {Number(totalClaims.incident_only).toLocaleString()}
                </Typography>
              }
              content={
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <div
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: colors.magentaRed[100],
                    }}
                  />

                  <Typography
                    variant="small"
                    style={{ fontWeight: '500', color: colors.neutral[900] }}
                  >
                    Incident Only
                  </Typography>
                </div>
              }
              onClick={() => onClickItem?.('incident_only')}
            />
          </div>
        </div>
      }
      labelColumnCount={2}
    />
  );
};
